/**
 * <LoginLinkPage> — l'ouverture du lien « Me connecter » reçu par e-mail (page).
 *
 * Le lien se consomme à l'ouverture : la personne n'a rien à saisir, la page dit où en est sa
 * connexion. Trois états, que le câblage choisit :
 *  - `connecting` : le lien est en cours d'échange — et, après « Continuer en tant que… », le
 *    compte ouvert se ferme pour laisser entrer celui du lien ;
 *  - `expired` : le lien a déjà servi ou a plus de dix minutes ;
 *  - `switchAccount` : le lien est bon mais un AUTRE compte est ouvert ici. On demande avant de
 *    basculer : un lien transmis par quelqu'un d'autre changerait sinon de compte sans un mot.
 *
 * Pas de retour : on arrive ici depuis un e-mail, il n'y a nulle part d'où revenir.
 */
import React from "react"

import { elide } from "../../../ports/primary/ui/formatters/childFormatter"
import { type UILoginLinkStatus } from "../../../ports/primary/ui/types"
import { Button } from "../../atoms/Buttons/Button"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { WaitingText } from "../../atoms/WaitingText"
import { AuthHeading } from "../../molecules/AuthHeading"
import { AuthTemplate } from "../../templates/AuthTemplate"

const CONNECTING = "Connexion en cours…"
const EXPIRED_TITLE = "Ce lien a expiré"
const EXPIRED_TEXT = "Un lien de connexion ne sert qu'une fois et reste valable 10 minutes."
const NEW_CODE_LABEL = "Recevoir un nouveau code"
const SWITCH_TITLE = "Changer de compte ?"

export type LoginLinkPageProps = {
    status: UILoginLinkStatus
    /** Le compte déjà ouvert sur l'appareil : son nom, à défaut son e-mail. Lu par `switchAccount`. */
    currentUserName?: string
    /** Le compte du lien, de la même façon. Lu par `switchAccount`. */
    linkUserName?: string
    /** « Recevoir un nouveau code » : retour à la connexion. */
    onRequestNewCode?: () => void
    /** « Continuer en tant que… » : fermer le compte ouvert et entrer dans celui du lien. */
    onContinueAsLinkUser?: () => void
    /** « Rester en tant que… » : garder le compte ouvert. */
    onStayAsCurrentUser?: () => void
    testID?: string
}

/** « que » ou « qu' », séparé du nom qu'il précède : dans la phrase, seul le nom passe en gras. */
function queBefore(name: string): string {
    const withName = elide(name, "que")
    return withName.slice(0, withName.length - name.trim().length)
}

function Name({ children }: { children: string }) {
    return (
        <Text weight="bold" color="text-primary">
            {children.trim()}
        </Text>
    )
}

export function LoginLinkPage(props: LoginLinkPageProps) {
    const {
        status,
        currentUserName = "",
        linkUserName = "",
        onRequestNewCode,
        onContinueAsLinkUser,
        onStayAsCurrentUser,
        testID,
    } = props

    return (
        <AuthTemplate centered testID={testID}>
            {status === "connecting" && <WaitingText>{CONNECTING}</WaitingText>}

            {status === "expired" && (
                <Stack gap="6">
                    <AuthHeading title={EXPIRED_TITLE}>{EXPIRED_TEXT}</AuthHeading>
                    <Button label={NEW_CODE_LABEL} fullWidth onPress={onRequestNewCode} />
                </Stack>
            )}

            {status === "switchAccount" && (
                <Stack gap="6">
                    <AuthHeading title={SWITCH_TITLE}>
                        {`Vous êtes connecté en tant ${queBefore(currentUserName)}`}
                        <Name>{currentUserName}</Name>
                        {`. Se connecter en tant ${queBefore(linkUserName)}`}
                        <Name>{linkUserName}</Name>
                        {" ?"}
                    </AuthHeading>
                    <Stack gap="2">
                        <Button
                            label={`Continuer en tant ${elide(linkUserName, "que")}`}
                            fullWidth
                            onPress={onContinueAsLinkUser}
                        />
                        <Button
                            variant="ghost"
                            label={`Rester en tant ${elide(currentUserName, "que")}`}
                            fullWidth
                            onPress={onStayAsCurrentUser}
                        />
                    </Stack>
                </Stack>
            )}
        </AuthTemplate>
    )
}
